import type { INavigationService } from '../../contracts'

/**
 * Web implementation of navigation service using React Router
 * Note: This requires the consuming app to provide the navigate function
 */
export class WebNavigationService implements INavigationService {
  private navigateFn: (path: string, options?: { replace?: boolean }) => void
  private location: { pathname: string; search: string }

  constructor(
    navigate: (path: string, options?: { replace?: boolean }) => void,
    location: { pathname: string; search: string }
  ) {
    this.navigateFn = navigate
    this.location = location
  }

  navigateToLogin(): void {
    this.navigateFn('/login', { replace: true })
  }

  navigateToHome(): void {
    this.navigateFn('/', { replace: true })
  }

  goBack(): void {
    // In web, we can use browser's back functionality
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.navigateToHome()
    }
  }

  getCurrentRoute(): { name: string; params?: Record<string, unknown> } {
    const pathname = this.location.pathname
    const searchParams = new URLSearchParams(this.location.search)
    const params: Record<string, unknown> = {}
    
    // Convert URLSearchParams to object
    searchParams.forEach((value, key) => {
      params[key] = value
    })

    return {
      name: pathname,
      params: Object.keys(params).length > 0 ? params : undefined,
    }
  }

  navigate(route: string, params?: Record<string, unknown>): void {
    let path = route
    
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      
      const queryString = searchParams.toString()
      if (queryString) {
        path += `?${queryString}`
      }
    }
    
    this.navigateFn(path)
  }

  reset(route: string, params?: Record<string, unknown>): void {
    // In web, reset is similar to navigate with replace
    this.navigate(route, params)
    // Clear browser history by replacing current entry
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      const fullPath = queryString ? `${route}?${queryString}` : route
      window.history.replaceState(null, '', fullPath)
    } else {
      window.history.replaceState(null, '', route)
    }
  }
}